import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Recaptcha, { type RecaptchaHandle } from "@/components/Recaptcha";
import { showQuoteSent } from "@/components/QuoteSentDialog";
import { COUNTRIES } from "@/data/countries";
import { FILTERS, today, validateQuote, type QuoteErrors, type QuoteFields } from "@/lib/quoteValidation";
import { useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";

interface QuoteFormProps {
  /** prefixes the fields' ids, for a page that has this form and the dialog's */
  idPrefix?: string;
  /** two fields to a row from md up (the Get Free Quote dialog); one column otherwise */
  twoColumns?: boolean;
  /** called once a request is sent (the dialog closes itself with it) */
  onSent?: () => void;
}

const EMPTY: QuoteFields = {
  fullName: "",
  email: "",
  country: "",
  age: "",
  phone: "",
  preferredCenter: "",
  treatment: "",
  travelDates: "",
  condition: "",
};

/** the message under a field that is wrong */
const Err = ({ id, text }: { id: string; text?: string }) =>
  text ? (
    <p id={id} role="alert" className="mt-1 px-1 text-xs font-medium text-red-600">
      {text}
    </p>
  ) : null;

/**
 * The Get Free Quote form: in the dialog (QuoteModal) on every page, and on
 * the Contact Us page itself. Boxes in the form's cream colours, each named by
 * its placeholder (the labels stay for screen readers). In two columns the
 * fields pair up in this order: name | email, country | age, phone | city,
 * treatment | travel dates, then the condition across the row, then the
 * "I'm not a robot" box | the submit button.
 *
 * What can be typed is filtered as it is typed, and every field is checked
 * on Submit, its own message under it (lib/quoteValidation).
 */
const QuoteForm = ({ idPrefix = "", twoColumns = false, onSent }: QuoteFormProps) => {
  const [formData, setFormData] = useState(EMPTY);
  const [countryCode, setCountryCode] = useState("");
  const [errors, setErrors] = useState<QuoteErrors>({});
  const [emailNoted, setEmailNoted] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot - real users never fill this
  const [dateFocused, setDateFocused] = useState(false);
  const [captcha, setCaptcha] = useState("");
  const captchaBox = useRef<RecaptchaHandle>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const fid = (key: string) => `${idPrefix}${key}`;
  const errId = (key: string) => `${idPrefix}${key}-error`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const found = validateQuote(formData, countryCode, { acceptEmail: emailNoted });
    setErrors(found);
    // asked "did you mean…?": the same address sent again is taken as typed
    if (found.email?.startsWith("Did you mean")) setEmailNoted(formData.email.trim());
    const first = Object.keys(found)[0];
    if (first) {
      toast.error("Please correct the highlighted fields.");
      formRef.current?.querySelector<HTMLElement>(`#${CSS.escape(fid(first === "code" ? "code" : first))}`)?.focus();
      return;
    }
    if (!captcha) {
      toast.error("Please tick “I'm not a robot”.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          fullName: formData.fullName.trim(),
          email: formData.email.trim(),
          phone: [countryCode.trim(), formData.phone.trim()].filter(Boolean).join(" "),
          // the mailer writes out the fields it knows; the age rides in the
          // condition too, so it reaches the team before the mailer learns it
          condition: formData.age
            ? `Age: ${formData.age}${formData.condition ? `\n\n${formData.condition}` : ""}`
            : formData.condition,
          recaptchaToken: captcha,
          // the page the enquiry came from, shown as "Source page" in the team email
          sourceUrl: window.location.href,
          sourcePage: document.title,
          website,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        toast.error(
          data.error ||
            "Sorry, we could not send your request. Please email care@myvaidyam.com."
        );
        captchaBox.current?.reset();
        return;
      }

      showQuoteSent({ ticketId: data.ticketId, name: formData.fullName, email: formData.email.trim() });

      setFormData(EMPTY);
      setCountryCode("");
      setErrors({});
      captchaBox.current?.reset();
      onSent?.();
    } catch {
      toast.error(
        "Network error. Please check your connection, or email care@myvaidyam.com."
      );
      captchaBox.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  const box =
    "h-12 rounded-xl border border-primary/10 bg-[#E9E3D0]/40 px-4 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0";
  const bad = "border-red-400 ring-1 ring-red-300";
  /** typing filters the value and clears that field's message */
  const set = (key: keyof QuoteFields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = FILTERS[key] ? FILTERS[key]!(e.target.value) : e.target.value;
    setFormData({ ...formData, [key]: value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };
  const aria = (key: keyof QuoteErrors) =>
    errors[key] ? { "aria-invalid": true as const, "aria-describedby": errId(key) } : {};
  const field = (children: ReactNode, wide = false) => <div className={wide ? "md:col-span-2" : undefined}>{children}</div>;

  // minmax(0,1fr), not an auto column: an <input> is about twenty characters
  // wide by default and an auto column never goes narrower, so on a narrow or
  // zoomed phone the form ran past the dialog's edge
  return (
    <form ref={formRef} noValidate onSubmit={handleSubmit} className={twoColumns ? "mt-2 grid grid-cols-[minmax(0,1fr)] gap-3 md:grid-cols-2" : "mt-2 space-y-3"}>
      {/* honeypot: hidden from humans, bots fill it and get silently dropped */}
      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }}
      />

      {field(
        <>
          <Label htmlFor={fid("fullName")} className="sr-only">Full Name</Label>
          <Input
            id={fid("fullName")}
            value={formData.fullName}
            onChange={set("fullName")}
            placeholder="Full Name *"
            autoComplete="name"
            maxLength={60}
            required
            className={`${box} ${errors.fullName ? bad : ""}`}
            {...aria("fullName")}
          />
          <Err id={errId("fullName")} text={errors.fullName} />
        </>
      )}

      {field(
        <>
          <Label htmlFor={fid("email")} className="sr-only">Email</Label>
          <Input
            id={fid("email")}
            type="email"
            value={formData.email}
            onChange={set("email")}
            placeholder="Email *"
            autoComplete="email"
            maxLength={100}
            required
            className={`${box} ${errors.email ? bad : ""}`}
            {...aria("email")}
          />
          <Err id={errId("email")} text={errors.email} />
        </>
      )}

      {/* every country; typing a letter jumps through the list */}
      {field(
        <>
          <Label htmlFor={fid("country")} className="sr-only">Country</Label>
          <Select
            value={formData.country}
            onValueChange={(value) => {
              setFormData({ ...formData, country: value });
              const country = COUNTRIES.find(([name]) => name === value);
              if (country) setCountryCode(country[1]);
              setErrors({ ...errors, country: undefined, code: undefined });
            }}
          >
            <SelectTrigger
              id={fid("country")}
              className={`${box} data-[placeholder]:text-muted-foreground focus:ring-2 focus:ring-primary/20 focus:ring-offset-0 ${errors.country ? bad : ""}`}
              {...aria("country")}
            >
              <SelectValue placeholder="Select your country *" />
            </SelectTrigger>
            <SelectContent position="popper" className="bg-card z-[100] max-h-72">
              {COUNTRIES.map(([name]) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Err id={errId("country")} text={errors.country} />
        </>
      )}

      {field(
        <>
          <Label htmlFor={fid("age")} className="sr-only">Age</Label>
          <Input
            id={fid("age")}
            value={formData.age}
            onChange={set("age")}
            placeholder="Age *"
            inputMode="numeric"
            maxLength={3}
            className={`${box} ${errors.age ? bad : ""}`}
            {...aria("age")}
          />
          <Err id={errId("age")} text={errors.age} />
        </>
      )}

      {/* the country code and the number in one box, the code set by the country */}
      {field(
        <>
          <Label htmlFor={fid("phone")} className="sr-only">Phone / WhatsApp Number</Label>
          <div
            className={`flex h-12 overflow-hidden rounded-xl border border-primary/10 bg-[#E9E3D0]/40 focus-within:ring-2 focus-within:ring-primary/20 ${
              errors.phone || errors.code ? bad : ""
            }`}
          >
            {/* filled in from the country chosen; the visitor can type one too */}
            <input
              id={fid("code")}
              type="tel"
              value={countryCode}
              onChange={(e) => {
                setCountryCode(FILTERS.code!(e.target.value));
                if (errors.code) setErrors({ ...errors, code: undefined });
              }}
              placeholder="+91"
              aria-label="Country code"
              autoComplete="tel-country-code"
              maxLength={5}
              className="w-16 shrink-0 border-r border-primary/10 bg-transparent px-2 text-center text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              {...aria("code")}
            />
            <input
              id={fid("phone")}
              type="tel"
              inputMode="tel"
              value={formData.phone}
              onChange={set("phone")}
              placeholder="Phone / WhatsApp Number *"
              autoComplete="tel-national"
              maxLength={18}
              required
              className="min-w-0 flex-1 bg-transparent px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              {...aria("phone")}
            />
          </div>
          {/* both, when both are missing or wrong */}
          <Err id={errId("code")} text={errors.code} />
          <Err id={errId("phone")} text={errors.phone} />
        </>
      )}

      {field(
        <>
          <Label htmlFor={fid("preferredCenter")} className="sr-only">Preferred Center/City</Label>
          <Input
            id={fid("preferredCenter")}
            value={formData.preferredCenter}
            onChange={set("preferredCenter")}
            placeholder="Preferred Center/City *"
            maxLength={80}
            className={`${box} ${errors.preferredCenter ? bad : ""}`}
            {...aria("preferredCenter")}
          />
          <Err id={errId("preferredCenter")} text={errors.preferredCenter} />
        </>
      )}

      {field(
        <>
          <Label htmlFor={fid("treatment")} className="sr-only">Treatment Interested In</Label>
          <Input
            id={fid("treatment")}
            value={formData.treatment}
            onChange={set("treatment")}
            placeholder="Treatment Interested In *"
            maxLength={100}
            className={`${box} ${errors.treatment ? bad : ""}`}
            {...aria("treatment")}
          />
          <Err id={errId("treatment")} text={errors.treatment} />
        </>
      )}

      {/* a date field cannot show a placeholder, so it is a text box until
          focused (or filled), then the date picker; no date before today */}
      {field(
        <>
          <Label htmlFor={fid("travelDates")} className="sr-only">Preferred Travel Dates</Label>
          <Input
            id={fid("travelDates")}
            type={dateFocused || formData.travelDates ? "date" : "text"}
            min={today()}
            value={formData.travelDates}
            onFocus={() => setDateFocused(true)}
            onBlur={() => setDateFocused(false)}
            onChange={set("travelDates")}
            placeholder="Preferred Travel Dates *"
            className={`${box} ${errors.travelDates ? bad : ""}`}
            {...aria("travelDates")}
          />
          <Err id={errId("travelDates")} text={errors.travelDates} />
        </>
      )}

      {field(
        <>
          <Label htmlFor={fid("condition")} className="sr-only">Describe Your Condition</Label>
          <Textarea
            id={fid("condition")}
            value={formData.condition}
            onChange={set("condition")}
            placeholder="Describe Your Condition * – your health concerns and goals..."
            rows={4}
            maxLength={1000}
            className={`${box} h-auto py-3 ${errors.condition ? bad : ""}`}
            {...aria("condition")}
          />
          <Err id={errId("condition")} text={errors.condition} />
        </>,
        true
      )}

      <div className="flex items-center">
        <Recaptcha ref={captchaBox} onToken={setCaptcha} />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="h-12 w-full self-center rounded-xl bg-[#C24E00] font-poppins text-base font-bold text-white shadow-lg hover:bg-[#A84300]"
      >
        Submit Request
      </Button>
    </form>
  );
};

export default QuoteForm;
